// api/squad.js - Squad Management API

import { apiClient } from './client';

class SquadAPI {
  constructor(client) {
    this.client = client;
  }

  // ========== BASIC CRUD ==========

  async getSquad(campaignId) {
    return this.client.request(`/api/squad?campaign_id=${campaignId}`);
  }

  async getSquadMember(memberId) {
    return this.client.request(`/api/squad/${memberId}`);
  }

  async createSquadMember(campaignId, name, rank, options = {}) {
    const memberData = {
      campaign_id: campaignId,
      name,
      rank,
      health: options.health || 100,
      max_health: options.max_health || 100,
      status: options.status || 'Ready',
      missions_completed: options.missions_completed || 0,
      nickname: options.nickname || null,
      class: options.class || null,
      experience: options.experience || 0,
      unlocked_skills: options.unlocked_skills || [],
    };

    if (options.loadout) {
      memberData.loadout = typeof options.loadout === 'string' 
        ? options.loadout 
        : JSON.stringify(options.loadout);
    }

    return this.client.request('/api/squad', {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
  }

  async updateSquadMember(memberId, updates) {
    // Handle arrays - stringify if needed
    const processedUpdates = { ...updates };
    
    if (updates.unlocked_skills && Array.isArray(updates.unlocked_skills)) {
      processedUpdates.unlocked_skills = JSON.stringify(updates.unlocked_skills);
    }
    
    if (updates.loadout && typeof updates.loadout !== 'string') {
      processedUpdates.loadout = JSON.stringify(updates.loadout);
    }

    return this.client.request(`/api/squad/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify(processedUpdates),
    });
  }

  async deleteSquadMember(memberId) {
    return this.client.request(`/api/squad/${memberId}`, {
      method: 'DELETE',
    });
  }

  // ========== HEALTH & STATUS ==========

  async healSquadMember(memberId, healthAmount) {
    const member = await this.getSquadMember(memberId);
    const newHealth = Math.min(member.health + healthAmount, member.max_health);
    
    const updates = { health: newHealth };
    if (newHealth === member.max_health) {
      updates.status = 'Ready';
    }
    
    return this.updateSquadMember(memberId, updates);
  }

  async batchUpdateSquad(updates) {
    const promises = updates.map(({ memberId, updates }) => 
      this.updateSquadMember(memberId, updates)
    );
    return Promise.all(promises);
  }

  // ========== RANK & PROGRESSION ==========

  async promoteMember(memberId, newRank) {
    return this.updateSquadMember(memberId, { rank: newRank });
  }

  async addExperience(memberId, xpAmount) {
    const member = await this.getSquadMember(memberId);
    const currentXp = member.experience || 0;
    const newXp = currentXp + xpAmount;
    
    return this.updateSquadMember(memberId, { experience: newXp });
  }

  // ========== CLASS SYSTEM ==========

  async updateNickname(memberId, nickname) {
    return this.updateSquadMember(memberId, { nickname });
  }

  async specializeSoldier(memberId, classId) {
    return this.updateSquadMember(memberId, {
      class: classId,
      experience: 0,
      unlocked_skills: []
    });
  }

  async unlockSkill(memberId, skillId) {
    const member = await this.getSquadMember(memberId);
    const currentSkills = Array.isArray(member.unlocked_skills) 
      ? member.unlocked_skills 
      : (member.unlocked_skills ? JSON.parse(member.unlocked_skills) : []);
    
    if (!currentSkills.includes(skillId)) {
      currentSkills.push(skillId);
    }
    
    return this.updateSquadMember(memberId, { 
      unlocked_skills: currentSkills 
    });
  }

  // ========== LOADOUT ==========

  async updateLoadout(memberId, loadout) {
    return this.updateSquadMember(memberId, { loadout });
  }
}

// Export singleton instance
export const squadAPI = new SquadAPI(apiClient);

// Also export class for custom instances
export default SquadAPI;